import { Http } from '@/services/http'
import { Injectable } from '@tanbo/vue-di-plugin'
import { InvokeThrottle } from '@/common/decorators/invoke-throttle'
import { ParamsClean } from '@/common/decorators/params-clean'
import { AuthStatusList } from '@/views/dashboard/_type/client-item'
import { getAreaByTerminalType } from '@/services/service-permission.service'
import {
  DUBA_BASELINE_REPAIR_ITEMS_KEY,
  invalidateBaselineRepairItemsCache
} from './baseline-repair-items-cache'

export interface BaseLineItem {
  /** 终端ID */
  device_id: string
  /** 计算机名 */
  computer_name: string
  /** 所属分组 */
  group_name: string
  /** 登记用户名 */
  user_mark: string
  /** 外网ip地址 */
  client_ip: string
  /** 内网ip地址 */
  system_ip: string
  /** 操作系统 */
  system_name: string
  /** 终端状态 1：在线，2：离线 */
  device_status: number
  /** 系统类型 1-windows 2-linux */
  system_type?: number
  /** 扫描时间 */
  check_time: number
  /** 不合规数量 */
  substandard_num: number
  /** 不合规数量（接口新字段，优先于 substandard_num 展示） */
  non_compliance_count?: number
  /** 授权状态集合 */
  auth_status?: AuthStatusList[]
  /** 前端字段 */
  area_type?: number
  /** 终端在线状态，与 device_status 一致，供 CmTable COMPUTER 列 */
  login_status?: number
}

/** 检测项统计行（列表 / 已忽略） */
export interface BaselineCheckItemStatRow {
  id: string
  check_item_id: string
  operator_uid: string
  operator_name: string
  create_time: string
  update_time: string
  /** 检测项名称（无缓存命中时兜底） */
  item_name?: string
  category_name?: string
  /** 不合规终端数（兼容旧字段） */
  substandard_terminal_count?: number
  /** 不合规终端数（优先展示） */
  non_compliance_count?: number
}

export interface FetchOptions {
  /** 页码 */
  page: number
  /** 每页数量 */
  limit: number
  /** 检查结果，1为合规，2为不合规，3为全部 */
  check_result: number
  /**
   * 仅展示「最后扫描时间距今超过 N 天」的终端（与统计卡「超过7天未扫描」对齐）。
   * 需 `/baselinecheck/result/list` 支持；未支持时参数可无害透传或由后端忽略。
   */
  stale_scan_gt_days?: number
  /** 排序,true-正序，false-倒序 */
  asc?: boolean
  /** 排序字段（端口号：port_num,终端数量：device_total） */
  sort?: string
  /** 模糊搜索 */
  search_word: string
  /** 系统类型,0-全部，1-windows,2-linux，3-windows server，4-mac */
  system_type?: number
  /** 分组，-1 表示全部 */
  group_id?: number
  permission_names?: string[]
  /** 按检测项（不合规项）筛选终端，值为 check_item_id */
  substandard_id?: string
  /** 超过X天未扫描筛选<br>> 0: 返回超过指定天数未扫描的终端（包括从未扫描） */
  unscanned_over_days?: number
}

/** 首页/抽屉内打开终端列表时的初始筛选（含是否隐藏「是否合规」下拉） */
export type BaselineTerminalDrawerInitialPatch = Partial<
  Pick<
    FetchOptions,
    | 'check_result'
    | 'system_type'
    | 'group_id'
    | 'substandard_id'
    | 'stale_scan_gt_days'
    | 'unscanned_over_days'
  >
> & {
  /** 从某检测项详情时锁定不合规+检测项，隐藏扫描结果/是否合规筛选项 */
  hideComplianceFilter?: boolean
  /** 终端列表抽屉标题中方括号内的说明文案（仅展示，不参与请求；与「全部终端 [xxx]」拼接） */
  drawerTitle?: string
  /** 从检测项列表点「修复」进入时，工具栏展示针对该检测项的批量修复（值为 check_item_id） */
  repairCheckItemId?: string
  /** 从检测项列表点「详情」或「修复」进入终端抽屉时，隐藏行内「扫描/详情」操作列 */
  hideTerminalRowActions?: boolean
}

export interface ScanParams {
  /** 选择类型,1-指定终端，2-指定分组 */
  select_type: number
  /** 终端设备id列表 */
  device_list: string[]
  /** 分组id */
  group_id: number
}

/** 已忽略检测项列表查询（与检测项 TOP 列表一致：page、limit） */
export interface BaselineIgnoredListParams {
  page: number
  limit: number
}

@Injectable()
export class BaseLineService {
  constructor(private http: Http) { }

  // eslint-disable-next-line no-magic-numbers
  @ParamsClean()
  @InvokeThrottle(800)
  getBaseLineData(data: FetchOptions) {
    return this.http.post<{
      list: BaseLineItem[],
      enable_total: number
      total: number
    }>('/baselinecheck/result/list', data).then(res => {
      res.list.forEach(v => {
        v.area_type = getAreaByTerminalType(v.system_type)
        v.login_status = v.device_status
      })
      return res
    })
  }

  /** 检测项 TOP 列表（按不合规终端数降序，后端未就绪时可能返回错误，由调用方 catch） */
  @ParamsClean()
  getCheckItemStatList(data: { page: number; limit: number; key_word?: string; sort?: string; asc?: boolean }) {
    return this.http.post<{ data: BaselineCheckItemStatRow[]; total: number }>(
      '/baselinecheck/check-item/list',
      data
    )
  }

  /** 已忽略检测项（page/limit 与 CmTable、check-item 列表一致；同时带 page_no/page_size 兼容旧接口） */
  @ParamsClean()
  getIgnoredCheckItemList(data: BaselineIgnoredListParams) {
    const { page, limit } = data
    return this.http.post<{ data: BaselineCheckItemStatRow[]; total: number }>(
      '/baselinecheck/ignored-items/list',
      { page, limit, page_no: page, page_size: limit }
    )
  }

  /** 批量忽略检测项 */
  batchIgnoreCheckItems(data: { check_item_ids: string[] }) {
    return this.http.post<{ task_id?: string }>('/baselinecheck/item/ignore', data)
  }

  /** 取消忽略（单个或批量） */
  cancelIgnoreCheckItems(data: { check_item_ids: string[] }) {
    return this.http.post('/baselinecheck/item/unignore', data)
  }

  getAllBaselineItems() {
    return this.http.post<{
      /** 基线检查所有检查项json数据 */
      check_items: string
    }>('/baselinecheck/check-items', {})
      .then(res => {
        localStorage.setItem(DUBA_BASELINE_REPAIR_ITEMS_KEY, res.check_items)
        invalidateBaselineRepairItemsCache()
      })
  }

  getBaseLineCheckCheck(data: ScanParams) {
    return this.http.post<{ task_id: string }>('/baselinecheck/check', data)
  }
}
